import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, addDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { 
  Building2, 
  Send, 
  CheckCircle, 
  AlertCircle, 
  DollarSign,
  Users,
  FileText
} from 'lucide-react';
import SEO from '../components/SEO';

const SponsorApply: React.FC = () => {
  const { currentUser } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [hasExistingApplication, setHasExistingApplication] = useState(false);
  const [checkingApplication, setCheckingApplication] = useState(false);

  const [formData, setFormData] = useState({
    // Şirket Bilgileri
    companyName: '',
    companyType: '',
    website: '',
    email: '',
    phone: '',
    address: '',
    
    // İletişim Bilgileri
    contactPerson: '',
    contactTitle: '',
    contactEmail: '',
    contactPhone: '',
    
    // Sponsorluk Detayları
    sponsorshipType: '',
    budget: '',
    duration: '',
    targetAudience: '',
    expectedReach: '',
    
    // Ek Bilgiler
    previousSponsorship: '',
    marketingGoals: '',
    specialRequirements: '',
    additionalInfo: ''
  });

  // Kullanıcının mevcut başvurusunu kontrol et
  useEffect(() => {
    const checkExistingApplication = async () => {
      if (!currentUser) {
        setCheckingApplication(false);
        setHasExistingApplication(false);
        return;
      }

      try {
        setCheckingApplication(true);
        const applicationsQuery = query(
          collection(db, 'sponsors'),
          where('userId', '==', currentUser.uid)
        );
        const applicationsSnapshot = await getDocs(applicationsQuery);
        
        if (!applicationsSnapshot.empty) {
          setHasExistingApplication(true);
        } else {
          setHasExistingApplication(false);
        }
      } catch (error) {
        // Hata yönetimi - loglanmaz güvenlik nedeniyle
        setHasExistingApplication(false);
      } finally {
        setCheckingApplication(false);
      }
    };

    checkExistingApplication();
  }, [currentUser]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;
    
    setIsSubmitting(true);
    
    try {
      const applicationData = {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        userName: currentUser.displayName || 'Anonim',
        ...formData,
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      await addDoc(collection(db, 'sponsors'), applicationData);
      
      setIsSubmitting(false);
      setSubmitStatus('success');
      
      // Form verilerini sıfırla
      setFormData({
        companyName: '',
        companyType: '',
        website: '',
        email: '',
        phone: '',
        address: '',
        contactPerson: '',
        contactTitle: '',
        contactEmail: '',
        contactPhone: '',
        sponsorshipType: '',
        budget: '',
        duration: '',
        targetAudience: '',
        expectedReach: '',
        previousSponsorship: '',
        marketingGoals: '',
        specialRequirements: '',
        additionalInfo: ''
      });
      
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 3000);
    } catch (error) {
      // Hata yönetimi - loglanmaz güvenlik nedeniyle
      setIsSubmitting(false);
      setSubmitStatus('error');
      
      setTimeout(() => setSubmitStatus('idle'), 3000);
    }
  };

  if (checkingApplication) {
    return (
      <div className="min-h-screen bg-dark-900 text-white pt-24 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Başvuru durumu kontrol ediliyor...</p>
        </div>
      </div>
    );
  }

  if (hasExistingApplication) {
    return (
      <div className="min-h-screen bg-dark-900 text-white pt-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-6" />
            <h1 className="text-3xl font-gaming font-bold text-white mb-4">
              Başvurunuz Alındı
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Sponsorluk başvurunuz başarıyla gönderildi. En kısa sürede değerlendirilecektir.
            </p>
            <div className="bg-dark-800 rounded-xl p-6 border border-dark-700">
              <h3 className="text-lg font-semibold text-white mb-4">Sonraki Adımlar</h3>
              <div className="space-y-3 text-left">
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                    <span className="text-xs font-bold text-white">1</span>
                  </div>
                  <span className="text-gray-300">Başvurunuz adminler tarafından incelenecek</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                    <span className="text-xs font-bold text-white">2</span>
                  </div>
                  <span className="text-gray-300">E-posta ile size geri dönüş yapılacak</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center">
                    <span className="text-xs font-bold text-white">3</span>
                  </div>
                  <span className="text-gray-300">Onay durumunuz profil sayfanızda görünecek</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO 
        title="Sponsorluk Başvurusu - Aedon Community" 
        description="Aedon Community'ye sponsor olmak için başvuru yapın. FiveM topluluğunda markanızı tanıtın ve büyüyün."
        keywords="sponsorluk, başvuru, FiveM, topluluk, reklam, Aedon Community"
      />
      
      <div className="min-h-screen bg-dark-900 text-white pt-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-gaming font-bold text-white mb-4">
              Sponsorluk Basvurusu
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Aedon Community'ye sponsor olmak için başvuru formunu doldurun
            </p>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-dark-800 rounded-xl border border-dark-700 overflow-hidden"
          >
            <div className="p-8">
              {!currentUser ? (
                <div className="text-center py-12">
                  <AlertCircle className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-4">
                    Giriş Yapmanız Gerekiyor
                  </h3>
                  <p className="text-gray-400 mb-6">
                    Sponsorluk başvurusu yapmak için önce giriş yapmalısınız
                  </p>
                  <a
                    href="/login"
                    className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-all duration-300 hover:scale-105 shadow-lg inline-flex items-center"
                  >
                    <Building2 className="w-5 h-5 mr-2" />
                    Giriş Yap
                  </a>
                </div>
              ) : (
                <form onSubmit={handleFormSubmit} className="space-y-8">
                  {/* Şirket Bilgileri */}
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-6 flex items-center">
                      <Building2 className="w-6 h-6 text-primary-400 mr-3" />
                      Şirket Bilgileri
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Şirket Adı *
                        </label>
                        <input
                          type="text"
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="Şirket adınızı girin"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Şirket Türü *
                        </label>
                        <select
                          name="companyType"
                          value={formData.companyType}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                          required
                        >
                          <option value="">Seçiniz</option>
                          <option value="oyun">Oyun Şirketi</option>
                          <option value="teknoloji">Teknoloji</option>
                          <option value="e-ticaret">E-Ticaret</option>
                          <option value="hizmet">Hizmet</option>
                          <option value="diger">Diğer</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Website
                        </label>
                        <input
                          type="url"
                          name="website"
                          value={formData.website}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="https://example.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          E-posta *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="info@example.com"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Telefon
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="+90 555 123 45 67"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Adres
                        </label>
                        <input
                          type="text"
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="Şirket adresiniz"
                        />
                      </div>
                    </div>
                  </div>

                  {/* İletişim Bilgileri */}
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-6 flex items-center">
                      <Users className="w-6 h-6 text-primary-400 mr-3" />
                      İletişim Bilgileri
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          İletişim Kişisi *
                        </label>
                        <input
                          type="text"
                          name="contactPerson"
                          value={formData.contactPerson}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="Ad Soyad"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Pozisyon
                        </label>
                        <input
                          type="text"
                          name="contactTitle"
                          value={formData.contactTitle}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="Marketing Müdürü"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          İletişim E-postası *
                        </label>
                        <input
                          type="email"
                          name="contactEmail"
                          value={formData.contactEmail}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="iletisim@example.com"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          İletişim Telefonu
                        </label>
                        <input
                          type="tel"
                          name="contactPhone"
                          value={formData.contactPhone}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="+90 555 123 45 67"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Sponsorluk Detayları */}
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-6 flex items-center">
                      <DollarSign className="w-6 h-6 text-primary-400 mr-3" />
                      Sponsorluk Detayları
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Sponsorluk Türü *
                        </label>
                        <select
                          name="sponsorshipType"
                          value={formData.sponsorshipType}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                          required
                        >
                          <option value="">Seçiniz</option>
                          <option value="ana">Ana Sponsor</option>
                          <option value="altin">Altın Sponsor</option>
                          <option value="gumus">Gümüş Sponsor</option>
                          <option value="bronz">Bronz Sponsor</option>
                          <option value="ozel">Özel Sponsorluk</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Bütçe Aralığı *
                        </label>
                        <select
                          name="budget"
                          value={formData.budget}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                          required
                        >
                          <option value="">Seçiniz</option>
                          <option value="0-1000">0 - 1.000 TL</option>
                          <option value="1000-5000">1.000 - 5.000 TL</option>
                          <option value="5000-10000">5.000 - 10.000 TL</option>
                          <option value="10000-25000">10.000 - 25.000 TL</option>
                          <option value="25000+">25.000+ TL</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Süre *
                        </label>
                        <select
                          name="duration"
                          value={formData.duration}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                          required
                        >
                          <option value="">Seçiniz</option>
                          <option value="1">1 Ay</option>
                          <option value="3">3 Ay</option>
                          <option value="6">6 Ay</option>
                          <option value="12">12 Ay</option>
                          <option value="24">24 Ay</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Hedef Kitle
                        </label>
                        <input
                          type="text"
                          name="targetAudience"
                          value={formData.targetAudience}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="18-35 yaş, oyun severler"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Beklenen Erişim
                        </label>
                        <input
                          type="text"
                          name="expectedReach"
                          value={formData.expectedReach}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="Günde 1000+ görüntüleme"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Ek Bilgiler */}
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-6 flex items-center">
                      <FileText className="w-6 h-6 text-primary-400 mr-3" />
                      Ek Bilgiler
                    </h3>
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Önceki Sponsorluk Deneyimi
                        </label>
                        <textarea
                          name="previousSponsorship"
                          value={formData.previousSponsorship}
                          onChange={handleInputChange}
                          rows={3}
                          className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                          placeholder="Daha önce sponsorluk yaptığınız projeleri anlatın"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Pazarlama Hedefleri
                        </label>
                        <textarea
                          name="marketingGoals"
                          value={formData.marketingGoals}
                          onChange={handleInputChange}
                          rows={3}
                          className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                          placeholder="Bu sponsorluk ile neyi hedefliyorsunuz?"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Özel Gereksinimler
                        </label>
                        <textarea
                          name="specialRequirements"
                          value={formData.specialRequirements}
                          onChange={handleInputChange}
                          rows={3}
                          className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                          placeholder="Özel istekleriniz veya gereksinimleriniz"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Ek Bilgiler
                        </label>
                        <textarea
                          name="additionalInfo"
                          value={formData.additionalInfo}
                          onChange={handleInputChange}
                          rows={4}
                          className="w-full px-4 py-3 bg-dark-700 border border-dark-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                          placeholder="Eklemek istediğiniz diğer bilgiler"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end space-x-4 pt-6 border-t border-dark-700">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-8 py-3 rounded-lg font-medium transition-all duration-300 hover:scale-105 shadow-lg flex items-center"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Gönderiliyor...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Başvuruyu Gönder
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>

          {/* Status Messages */}
          <AnimatePresence>
            {submitStatus === 'success' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="mt-6 p-4 bg-green-900/20 border border-green-500 rounded-lg text-green-400 text-center"
              >
                <CheckCircle className="w-6 h-6 mx-auto mb-2" />
                Başvurunuz başarıyla gönderildi! En kısa sürede değerlendirilecektir.
              </motion.div>
            )}
            {submitStatus === 'error' && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="mt-6 p-4 bg-red-900/20 border border-red-500 rounded-lg text-red-400 text-center"
              >
                <AlertCircle className="w-6 h-6 mx-auto mb-2" />
                Başvuru gönderilirken bir hata oluştu. Lütfen tekrar deneyin.
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
};

export default SponsorApply;
